// @ts-nocheck

import React, { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

interface Coupon {
  id: string;
  code: string;
  description: string;
  discount_type: string;
  discount_value: number;
  is_active: boolean;
  discount_per_team: boolean;
  created_at: string;
}

export default function EventCouponsManagement() {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);

  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [discountType, setDiscountType] = useState("percentage");
  const [discountValue, setDiscountValue] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [discountPerTeam, setDiscountPerTeam] = useState(false);

  const fetchCoupons = async () => {
    const { data, error } = await supabase
      .from("event_coupons")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) toast.error("Failed to load coupons");
    else setCoupons(data || []);
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const handleEdit = (coupon: Coupon) => {
    setEditingCoupon(coupon);
    setCode(coupon.code);
    setDescription(coupon.description);
    setDiscountType(coupon.discount_type === "%" ? "percentage" : "fixed");
    setDiscountValue(coupon.discount_value);
    setIsActive(coupon.is_active);
    setDiscountPerTeam(coupon.discount_per_team);
  };

  const handleCancelEdit = () => {
    setEditingCoupon(null);
    setCode("");
    setDescription("");
    setDiscountType("percentage");
    setDiscountValue(0);
    setIsActive(true);
    setDiscountPerTeam(false);
  };

  const handleDelete = async (couponId: string) => {
    if (!confirm("Are you sure you want to delete this coupon?")) return;
    
    const { error } = await supabase
      .from("event_coupons")
      .delete()
      .eq("id", couponId);

    if (error) {
      toast.error("Failed to delete coupon");
      return;
    }

    toast.success("Coupon deleted");
    fetchCoupons();
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    // Map UI values to database constraint values
    const dbDiscountType = discountType === "percentage" ? "%" : "$";
    
    if (editingCoupon) {
      // Update existing coupon
      const { error } = await supabase
        .from("event_coupons")
        .update({
          code,
          description,
          discount_type: dbDiscountType,
          discount_value: discountValue,
          is_active: isActive,
          discount_per_team: discountPerTeam,
        })
        .eq("id", editingCoupon.id);
      
      setLoading(false);
      if (error) {
        toast.error("Error updating coupon");
        return;
      }
      
      toast.success("Coupon updated");
    } else {
      // Create new coupon
      const { error } = await supabase.from("event_coupons").insert([
        {
          code,
          description,
          discount_type: dbDiscountType,
          discount_value: discountValue,
          is_active: isActive,
          discount_per_team: discountPerTeam,
        },
      ]);
      setLoading(false);
      if (error) {
        toast.error("Error creating coupon");
        return;
      }

      toast.success("Coupon created");
    }
    
    handleCancelEdit();
    fetchCoupons();
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{editingCoupon ? "Edit Coupon" : "Create New Coupon"}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Coupon Code</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value)} />
          </div>
          <div>
            <Label>Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div>
            <Label>Discount Type</Label>
            <select
              className="w-full border rounded p-2"
              value={discountType}
              onChange={(e) => setDiscountType(e.target.value)}
            >
              <option value="percentage">Percentage</option>
              <option value="fixed">Fixed</option>
            </select>
          </div>
          <div>
            <Label>Discount Value</Label>
            <Input
              type="number"
              value={discountValue}
              onChange={(e) => setDiscountValue(parseFloat(e.target.value))}
            />
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox checked={isActive} onCheckedChange={(checked) => setIsActive(checked === true)} />
            <Label>Active</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox checked={discountPerTeam} onCheckedChange={(checked) => setDiscountPerTeam(checked === true)} />
            <Label>Discount Per Team</Label>
            <span className="text-xs text-muted-foreground ml-2">
              (Automatically multiplies discount by number of teams in registration)
            </span>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSubmit} disabled={loading}>
              {loading ? (editingCoupon ? "Updating..." : "Creating...") : (editingCoupon ? "Update Coupon" : "Create Coupon")}
            </Button>
            {editingCoupon && (
              <Button onClick={handleCancelEdit} variant="outline">
                Cancel
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>All Coupons</CardTitle>
        </CardHeader>
        <CardContent>
          {coupons.length === 0 ? (
            <p className="text-muted-foreground">No coupons found.</p>
          ) : (
            <ul className="space-y-2">
              {coupons.map((coupon) => (
                <li key={coupon.id} className="flex items-center justify-between p-3 border rounded">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <strong className="text-lg">{coupon.code}</strong>
                      <span className="text-muted-foreground">–</span>
                      <span className="font-semibold">{coupon.discount_value}{coupon.discount_type}</span>
                      {coupon.discount_per_team && (
                        <Badge variant="secondary">Per Team</Badge>
                      )}
                      <span>{coupon.is_active ? "✅" : "❌"}</span>
                    </div>
                    {coupon.description && (
                      <p className="text-sm text-muted-foreground mt-1">{coupon.description}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(coupon)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(coupon.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

